import type React from "react";
import {useMemo, useState} from "react";
import {observer} from "mobx-react-lite";
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography} from "@mui/material";
import {PATH_SEP} from "~/constants";
import {useStores} from "~/stores";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import type {TaxonomyChangeRow} from "~/pages/taxonomy-builder-v2/services/taxonomyChanges";
import {incompleteCategories} from "~/pages/taxonomy-builder-v2/services/taxonomyCompleteness";
import {useSendTaxonomyForApprovalMutation} from "~/pages/taxonomy-builder-v2/hooks/useSendTaxonomyForApprovalMutation";
import {YourChangesTable} from "./YourChangesTable";

type YourChangesDialogProps = {
    rows: TaxonomyChangeRow[];
    taxonomy: m_taxonomy.FullSerializer;
    onClose: () => void;
};

/**
 * The draft's changes, opened from "Send for approval": one row per changed category, compared with the
 * taxonomy's first saved state (the backend's baseline). Sending takes optional notes for the approvers,
 * like the current builder's confirm dialog, and is blocked while categories stop above the taxonomy's
 * depth (unless the profile allows that). Mount it fresh for each opening.
 */
export const YourChangesDialog: React.FC<YourChangesDialogProps> = observer(({
                                                                                 rows,
                                                                                 taxonomy,
                                                                                 onClose,
                                                                             }) => {
    const {p} = useStores();
    const [notes, setNotes] = useState('');
    const {mutate, isPending} = useSendTaxonomyForApprovalMutation();

    const incomplete = useMemo(
        () => incompleteCategories(taxonomy.diff_tree, taxonomy.size),
        [taxonomy.diff_tree, taxonomy.size],
    );
    // As in the current builder: a profile setting allows it, and one taxonomy can be let through by id.
    const blocked = incomplete.length > 0
        && !p.p.allowIncompleteTaxonomy
        && taxonomy.id !== p.p.hackTaxonomyToHideApprovalError;

    const send = () => mutate({taxonomy, notes: notes.trim()}, {onSuccess: onClose});

    return (
        <Dialog open onClose={isPending ? undefined : onClose} maxWidth="xl" fullWidth>
            <DialogTitle>Your changes</DialogTitle>
            <DialogContent>
                {/* A fixed height: the table scrolls inside, and an opened row's picture has room. */}
                <Stack gap={2} sx={{height: '75vh', pt: 0.5}}>
                    <Stack sx={{flex: 1, minHeight: 0}}>
                        <YourChangesTable
                            rows={rows}
                            tree={taxonomy.diff_tree}
                            isLoading={false}
                            title={`${rows.length} ${rows.length === 1 ? 'changed category' : 'changed categories'}`}
                        />
                    </Stack>
                    {blocked
                        ? (
                            <Alert severity="error" sx={{maxHeight: 160, overflow: 'auto', flexShrink: 0}}>
                                Every category needs sub-categories down to L{taxonomy.size} before the taxonomy can
                                be sent for approval. Missing a level:
                                {incomplete.map(path => (
                                    <Typography key={path.join(PATH_SEP)} variant="body2" fontWeight={600}>
                                        {path.join(PATH_SEP)}
                                    </Typography>
                                ))}
                            </Alert>
                        )
                        : (
                            <Stack gap={1} sx={{flexShrink: 0}}>
                                <Typography variant="body2" color="text.secondary">
                                    The taxonomy can't be changed until the approval is complete.
                                </Typography>
                                <TextField
                                    label="Notes for the approvers"
                                    placeholder="Optional"
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                    multiline
                                    minRows={2}
                                    fullWidth
                                    size="small"
                                />
                            </Stack>
                        )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isPending}>Cancel</Button>
                <Button variant="contained" onClick={send} loading={isPending} disabled={blocked}>
                    Send for approval
                </Button>
            </DialogActions>
        </Dialog>
    );
});
